import React, { useState, useEffect, useCallback, useRef } from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import moment from 'moment';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../hooks/useSocket';
import SessionModal from '../components/SessionModal';
import SessionCard from '../components/SessionCard';
import AssessmentBasisPicker from '../components/AssessmentBasisPicker';
import { assessmentBasisLabel } from '../utils/assessmentBasis';

const TIMETABLE_HISTORY_DAYS = 90;
const TIMETABLE_FUTURE_DAYS = 90;
const MAX_RANGE_DAYS = 31; // longest From→To span the Home list will load

// Status pill for the import review table/cards — uses the app's own
// WCAG-verified pill tokens instead of raw Bootstrap bg-success/bg-danger,
// so it matches the rest of the UI in both light and dark mode.
function ImportStatusPill({ row, checked }) {
  const variants = {
    conflict: { bg: 'var(--tmis-pill-late-bg)', color: 'var(--tmis-pill-late-text)', icon: 'bi-exclamation-triangle-fill', label: 'Conflict' },
    missing:  { bg: 'var(--tmis-pill-absent-bg)', color: 'var(--tmis-pill-absent-text)', icon: 'bi-x-circle-fill', label: 'Missing' },
    error:    { bg: 'var(--tmis-pill-absent-bg)', color: 'var(--tmis-pill-absent-text)', icon: 'bi-x-circle-fill', label: 'Error' },
    available:{ bg: 'var(--tmis-pill-present-bg)', color: 'var(--tmis-pill-present-text)', icon: 'bi-check-circle-fill', label: 'Available' },
    pending:  { bg: 'var(--tmis-badge-ended-bg)', color: 'var(--tmis-badge-ended-text)', icon: 'bi-hourglass-split', label: 'Pending' },
  };
  const key = ['conflict', 'missing', 'error'].includes(row.status) ? row.status : (checked ? 'available' : 'pending');
  const v = variants[key];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      background: v.bg, color: v.color, borderRadius: 20,
      padding: '3px 10px', fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap',
    }}>
      <i className={`bi ${v.icon}`} />{v.label}
    </span>
  );
}

function timeToMinutesValue(time) {
  if (!time) return null;
  const parts = String(time).split(':').map(Number);
  if (parts.length < 2 || parts.some(Number.isNaN)) return null;
  return (parts[0] * 60) + parts[1];
}

function formatTimetableIntakes(value) {
  if (Array.isArray(value)) return value.filter(Boolean).join(', ');
  if (!value) return '';

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed.filter(Boolean).join(', ');
    } catch {
      // The API normally returns an array; keep a non-JSON value readable.
    }
  }

  return String(value);
}

function formatTimetableLoginTime(value) {
  return value ? moment(value).format('HH:mm') : '';
}

function timetableSessionStatus(session) {
  if (session.status === 'ongoing') return 'ongoing';
  if (session.status === 'ended') return 'ended';
  return 'scheduled';
}

function timetableSessionStatusLabel(status) {
  return status === 'ongoing' ? 'Ongoing' : status === 'ended' ? 'Ended' : 'Scheduled';
}

// Assign overlapping sessions to separate horizontal lanes so classes in
// different venues remain visible when their times intersect.
function timetableEventLanes(daySessions) {
  const layouts = new Map();
  const ordered = [...daySessions].sort((a, b) => {
    const startA = timeToMinutesValue(a.start_time) ?? 0;
    const startB = timeToMinutesValue(b.start_time) ?? 0;
    return startA - startB || (timeToMinutesValue(a.end_time) ?? startA) - (timeToMinutesValue(b.end_time) ?? startB);
  });

  const groups = [];
  ordered.forEach(session => {
    const start = timeToMinutesValue(session.start_time) ?? 0;
    const end = Math.max(start + 60, timeToMinutesValue(session.end_time) ?? start + 60);
    const group = groups[groups.length - 1];
    if (!group || start >= group.end) {
      groups.push({ end, sessions: [{ session, start, end }] });
    } else {
      group.end = Math.max(group.end, end);
      group.sessions.push({ session, start, end });
    }
  });

  groups.forEach(group => {
    const laneEnds = [];
    group.sessions.forEach(({ session, start, end }) => {
      let lane = laneEnds.findIndex(laneEnd => laneEnd <= start);
      if (lane === -1) lane = laneEnds.length;
      laneEnds[lane] = end;
      layouts.set(session.id, { lane, laneCount: 1 });
    });
    const laneCount = Math.max(1, laneEnds.length);
    group.sessions.forEach(({ session }) => { layouts.get(session.id).laneCount = laneCount; });
  });

  return layouts;
}

export default function HomePage() {
  const { can, user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(moment().format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));
  const [showModal, setShowModal] = useState(false);
  const [editSession, setEditSession] = useState(null);
  const [createDefaults, setCreateDefaults] = useState(null);
  const [mode, setMode] = useState('date'); // 'date' | 'upcoming'
  const [sessionType, setSessionType] = useState(''); // '' | 'training' | 'assessment'
  const [timetableWeekStart, setTimetableWeekStart] = useState(() => moment().startOf('isoWeek'));
  const [selectedTimetableSessionId, setSelectedTimetableSessionId] = useState(null);
  const importFileRef = useRef(null);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importChecking, setImportChecking] = useState(false);
  const [importSaving, setImportSaving] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importEmpty, setImportEmpty] = useState(null);
  const [expandedImportRows, setExpandedImportRows] = useState(new Set());

  function toggleImportRow(rowNumber) {
    setExpandedImportRows(prev => {
      const next = new Set(prev);
      if (next.has(rowNumber)) next.delete(rowNumber);
      else next.add(rowNumber);
      return next;
    });
  }

  // Rows needing attention (missing fields, errors, conflicts) start expanded
  // so the user sees what to fix; clean rows start collapsed to keep a
  // multi-row import from turning into a wall of open forms.
  function expandAttentionRows(rows) {
    setExpandedImportRows(new Set((rows || []).filter(r => r.status && r.status !== 'ready').map(r => r.row)));
  }

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      if (mode === 'upcoming') {
        const today = moment().startOf('day');
        const res = await api.get('/api/sessions', {
          params: {
            start_date: today.clone().subtract(TIMETABLE_HISTORY_DAYS, 'days').format('YYYY-MM-DD'),
            end_date: today.clone().add(TIMETABLE_FUTURE_DAYS, 'days').format('YYYY-MM-DD'),
            session_type: sessionType || undefined,
          }
        });
        const timetableSessions = (res.data.data || [])
          .filter(s => s.status !== 'cancelled')
          .sort((a, b) => (a.session_date + a.start_time).localeCompare(b.session_date + b.start_time));
        setSessions(timetableSessions);
      } else {
        const res = await api.get('/api/sessions', {
          params: { start_date: startDate, end_date: endDate, session_type: sessionType || undefined },
        });
        // Server returns newest date first; a range reads better as a schedule.
        const ranged = (res.data.data || [])
          .sort((a, b) => (a.session_date + a.start_time).localeCompare(b.session_date + b.start_time));
        setSessions(ranged);
      }
    } catch { toast.error('Failed to load sessions.'); }
    finally { setLoading(false); }
  }, [startDate, endDate, mode, sessionType]);

  // Keep the range valid (From <= To) and cap it so a huge range can't mount
  // hundreds of live-polling session cards at once.
  function changeStartDate(value) {
    if (!value) return;
    setStartDate(value);
    if (value > endDate) setEndDate(value);
    else if (moment(endDate).diff(moment(value), 'days') > MAX_RANGE_DAYS) {
      setEndDate(moment(value).add(MAX_RANGE_DAYS, 'days').format('YYYY-MM-DD'));
    }
  }
  function changeEndDate(value) {
    if (!value) return;
    setEndDate(value);
    if (value < startDate) setStartDate(value);
    else if (moment(value).diff(moment(startDate), 'days') > MAX_RANGE_DAYS) {
      setStartDate(moment(value).subtract(MAX_RANGE_DAYS, 'days').format('YYYY-MM-DD'));
    }
  }

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  // Bring the selected session's existing detail card and action buttons into
  // view after a timetable event is selected, especially on small screens.
  useEffect(() => {
    if (!selectedTimetableSessionId) return undefined;
    const frame = window.requestAnimationFrame(() => {
      const details = Array.from(document.querySelectorAll(
        `[data-timetable-details="${selectedTimetableSessionId}"]`,
      )).find(element => element.getClientRects().length > 0);
      if (details) {
        details.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'nearest',
        });
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [selectedTimetableSessionId]);

  // Real-time socket: session list events → re-fetch the list
  // session_updated also carries { id, status } so we can patch in-place
  // without a full re-fetch, but re-fetching is simpler and more reliable.
  useSocket({
    connect: () => fetchSessions(),
    session_created: () => fetchSessions(),
    session_updated: (data) => {
      // Patch the status in-place first (instant badge flip)
      if (data?.id && data?.status) {
        setSessions(prev => prev.map(s =>
          s.id === data.id ? { ...s, status: data.status } : s
        ));
      }
      // Then re-fetch to get full updated data
      fetchSessions();
    },
    session_deleted: () => fetchSessions(),
    session_ended:   (data) => {
      // Patch ended status in-place immediately
      if (data?.id) {
        setSessions(prev => prev.map(s =>
          s.id === data.id ? { ...s, status: 'ended' } : s
        ));
      }
      fetchSessions();
    },
  });

  function openCreate(dateOverride, startTimeOverride) {
    setEditSession(null);
    setCreateDefaults(dateOverride ? {
      date: dateOverride,
      startTime: startTimeOverride || '',
    } : null);
    setShowModal(true);
  }
  function openEdit(s)  { setEditSession(s);    setShowModal(true); }
  function openUpcoming() {
    setTimetableWeekStart(moment().startOf('isoWeek'));
    setSelectedTimetableSessionId(null);
    setMode('upcoming');
  }

  async function handleDownloadTemplate() {
    setDownloadingTemplate(true);
    try {
      const res = await api.get('/api/sessions/template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'sessions_template.xlsx'; a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download template.');
    } finally {
      setDownloadingTemplate(false);
    }
  }

  async function handleImport(e) {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    setImporting(true);
    try {
      const res = await api.post('/api/sessions/import/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (!res.data.rows?.length) {
        setImportEmpty({ filename: res.data.filename || file.name });
      } else {
        setImportPreview(res.data);
        expandAttentionRows(res.data.rows);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Import preview failed.');
    } finally {
      setImporting(false);
      importFileRef.current.value = '';
    }
  }

  function updateImportRow(rowNumber, field, value) {
    setImportPreview(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        checked: false,
        canImport: false,
        conflicts: [],
        errorDetails: [],
        blankFieldRows: [],
        rows: (prev.rows || []).map(row => {
          if (row.row !== rowNumber) return row;
          const next = { ...row, [field]: value, status: 'ready', missing: [], errors: [], conflicts: [] };
          if (field === 'session_type' && value !== 'assessment') {
            next.force_present = false;
            next.assessment_basis = '';
          }
          return next;
        }),
      };
    });
  }

  async function handleCheckImportAvailability() {
    if (!importPreview?.rows?.length) return;
    setImportChecking(true);
    try {
      const res = await api.post('/api/sessions/import/check', { rows: importPreview.rows });
      setImportPreview(prev => ({ ...prev, ...res.data, filename: prev?.filename, options: prev?.options }));
      expandAttentionRows(res.data.rows);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Availability check failed.');
    } finally {
      setImportChecking(false);
    }
  }

  async function handleConfirmImport() {
    if (!importPreview?.rows?.length) return;
    setImportSaving(true);
    try {
      const res = await api.post('/api/sessions/import/confirm', { rows: importPreview.rows });
      setImportResult(res.data);
      setImportPreview(null);
      fetchSessions();
    } catch (err) {
      if (err.response?.data?.rows) {
        setImportPreview(prev => ({ ...prev, ...err.response.data, filename: prev?.filename, options: prev?.options }));
        expandAttentionRows(err.response.data.rows);
      }
      toast.error(err.response?.data?.message || 'Import failed.');
    } finally {
      setImportSaving(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this session?')) return;
    try {
      const res = await api.delete(`/api/sessions/${id}`);
      toast.success(res.data.message || 'Session deleted.');
      fetchSessions();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed.'); }
  }

  async function handleDuplicate(id) {
    try {
      await api.post(`/api/sessions/${id}/duplicate`);
      toast.success('Session duplicated to next day.');
      fetchSessions();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed.'); }
  }

  async function handleEndClass(id) {
    if (!window.confirm('End this session? This will finalize attendance and send reports.')) return;
    try {
      await api.post(`/api/sessions/${id}/end`);
      toast.success('Session ended. Report will be emailed.');
      fetchSessions();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed.'); }
  }

  const timetableDays = Array.from({ length: 7 }, (_, index) => timetableWeekStart.clone().add(index, 'days'));
  const timetableDayKeys = new Set(timetableDays.map(day => day.format('YYYY-MM-DD')));
  const timetableWeekSessions = sessions
    .filter(s => timetableDayKeys.has(s.session_date))
    .sort((a, b) => (a.session_date + a.start_time).localeCompare(b.session_date + b.start_time));
  const timetableSessionsByDate = timetableWeekSessions.reduce((acc, session) => {
    if (!acc[session.session_date]) acc[session.session_date] = [];
    acc[session.session_date].push(session);
    return acc;
  }, {});
  const timetableEventLayoutsByDate = Object.keys(timetableSessionsByDate).reduce((acc, dayKey) => {
    acc[dayKey] = timetableEventLanes(timetableSessionsByDate[dayKey]);
    return acc;
  }, {});
  const timetableEarliestStartHour = timetableWeekSessions.reduce((earliest, session) => {
    const startMinutes = timeToMinutes(session.start_time);
    return startMinutes !== null ? Math.min(earliest, Math.floor(startMinutes / 60)) : earliest;
  }, 6);
  const timetableStartHour = Math.max(0, timetableEarliestStartHour);
  const timetableLatestEndHour = timetableWeekSessions.reduce((latest, session) => {
    const endMinutes = timeToMinutes(session.end_time);
    return endMinutes !== null ? Math.ceil(endMinutes / 60) : latest;
  }, 22);
  const timetableEndHour = Math.min(24, Math.max(22, timetableLatestEndHour));
  const timetableSlots = Array.from({ length: timetableEndHour - timetableStartHour }, (_, index) => timetableStartHour + index);
  const timetableSelectedSession = sessions.find(s => s.id === selectedTimetableSessionId);
  const timetableToday = moment().startOf('day');
  const timetableCurrentWeek = timetableToday.clone().startOf('isoWeek');
  const timetableHistoryStart = timetableToday.clone().subtract(TIMETABLE_HISTORY_DAYS, 'days').startOf('isoWeek');
  const timetableFutureEnd = timetableToday.clone().add(TIMETABLE_FUTURE_DAYS, 'days').endOf('isoWeek');
  const timetablePreviousWeek = timetableWeekStart.clone().subtract(1, 'week');
  const timetableNextWeek = timetableWeekStart.clone().add(1, 'week');
  const timetableCanGoPrevious = !timetablePreviousWeek.isBefore(timetableHistoryStart, 'day');
  const timetableCanGoNext = !timetableNextWeek.isAfter(timetableFutureEnd, 'day');
  const timetableDatePickerValue = timetableWeekStart.isSame(timetableCurrentWeek, 'day')
    ? timetableToday.format('YYYY-MM-DD')
    : timetableWeekStart.format('YYYY-MM-DD');
  const timetableWeekEnd = timetableWeekStart.clone().add(6, 'days');
  const timetableRangeLabel = timetableWeekStart.year() === timetableWeekEnd.year()
    ? `${timetableWeekStart.format('D MMM')} - ${timetableWeekEnd.format('D MMM YYYY')}`
    : `${timetableWeekStart.format('D MMM YYYY')} - ${timetableWeekEnd.format('D MMM YYYY')}`;

  function timetableDayState(day) {
    if (day.isBefore(timetableToday, 'day')) return 'is-past';
    if (day.isSame(timetableToday, 'day')) return 'is-today';
    return 'is-future';
  }

  function timeToMinutes(time) {
    if (!time) return null;
    const parts = String(time).split(':').map(Number);
    if (parts.length < 2 || parts.some(Number.isNaN)) return null;
    return (parts[0] * 60) + parts[1];
  }

  function timetableTimeLabel(time) {
    return time ? moment(time, ['HH:mm:ss', 'HH:mm']).format('h:mm A') : 'TBA';
  }

  function timetableEventPosition(session, layout = { lane: 0, laneCount: 1 }) {
    const gridStart = timetableStartHour * 60;
    const start = Math.max(gridStart, timeToMinutes(session.start_time) ?? gridStart);
    const end = Math.min(timetableEndHour * 60, Math.max(start + 60, timeToMinutes(session.end_time) ?? start + 60));
    const rowHeight = 74;
    return {
      top: `${((start - gridStart) / 60) * rowHeight + 6}px`,
      height: `${Math.max(60, ((end - start) / 60) * rowHeight - 12)}px`,
      left: `calc(${(layout.lane * 100) / layout.laneCount}% + 6px)`,
      width: `calc(${100 / layout.laneCount}% - 12px)`,
    };
  }

  function timetableSlotHasSession(daySessions, hour) {
    const slotStart = hour * 60;
    const slotEnd = slotStart + 60;
    return daySessions.some(session => {
      const start = timeToMinutes(session.start_time);
      const end = timeToMinutes(session.end_time) ?? ((start ?? slotStart) + 60);
      return start !== null && start < slotEnd && end > slotStart;
    });
  }

  function moveTimetableWeek(offset) {
    if (offset < 0 && !timetableCanGoPrevious) return;
    if (offset > 0 && !timetableCanGoNext) return;
    setSelectedTimetableSessionId(null);
    setTimetableWeekStart(prev => prev.clone().add(offset, 'weeks'));
  }

  function selectTimetableDate(value) {
    const selectedDate = moment(value, 'YYYY-MM-DD', true);
    if (!selectedDate.isValid()
      || selectedDate.isBefore(timetableHistoryStart, 'day')
      || selectedDate.isAfter(timetableFutureEnd, 'day')) return;
    setSelectedTimetableSessionId(null);
    setTimetableWeekStart(selectedDate.startOf('isoWeek'));
  }

  const importRows = importPreview?.rows || [];
  const importHasLocalMissing = importRows.some(row =>
    !row.intake || !row.module || !row.venue || !row.session_date || !row.start_time || !row.end_time || !row.session_type || row.end_time <= row.start_time
  );
  const importCanProceed = importRows.length > 0 && !importHasLocalMissing && !importChecking && !importSaving;
  const importCanSave = !!importPreview?.checked && !!importPreview?.canImport && !importSaving && !importChecking;
  const importAttentionCount = importRows.filter(row => row.status && row.status !== 'ready').length;
  const importModulesFor = (intake) => importPreview?.options?.modulesByIntake?.[intake] || [];
  const importModuleLabelFor = (intake, code) => importModulesFor(intake).find(option => option.value === code)?.label || code;
  const importIntakeOptions = (importPreview?.options?.intakes || []).map(value => ({ value, label: value }));
  const importVenueOptions = (importPreview?.options?.venues || []).map(value => ({ value, label: value }));
  const importTrainerOptions = (importPreview?.options?.trainers || []).map(value => ({ value, label: value }));

  function renderTimetableDetails(variant = '') {
    if (!timetableSelectedSession) return null;
    return (
      <div
        className={`timetable-details ${variant}`}
        id={`timetable-details-${timetableSelectedSession.id}`}
        data-timetable-details={timetableSelectedSession.id}
      >
        <div className="timetable-details-heading">
          <div><span>Details and actions</span></div>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => setSelectedTimetableSessionId(null)}
          >
            <i className="bi bi-x-lg me-1" aria-hidden="true" />Close details
          </button>
        </div>
        <SessionCard
          session={timetableSelectedSession}
          onEdit={() => openEdit(timetableSelectedSession)}
          onDelete={() => handleDelete(timetableSelectedSession.id)}
          onDuplicate={() => handleDuplicate(timetableSelectedSession.id)}
          onEnd={() => handleEndClass(timetableSelectedSession.id)}
          canEdit={can('sessions', 'update')}
          canDelete={can('sessions', 'delete')}
          canCreate={can('sessions', 'create')}
          user={user}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="page-header attendance-home-header d-flex justify-content-between align-items-start flex-wrap gap-3">
        <div>
          <h1>Take Attendance</h1>
          <p>Manage and monitor today's sessions</p>
        </div>
        <div className="attendance-home-actions d-flex gap-2 flex-wrap">
          {can('sessions', 'create') && (
            <>
              <button
                className="btn btn-tmis-outline d-flex align-items-center gap-2"
                onClick={handleDownloadTemplate}
                disabled={downloadingTemplate}
              >
                {downloadingTemplate
                  ? <span className="spinner-border spinner-border-sm" />
                  : <i className="bi bi-file-earmark-excel" />}
                Template
              </button>
              <input
                type="file"
                ref={importFileRef}
                accept=".xlsx,.xls,.csv"
                className="d-none"
                onChange={handleImport}
              />
              <button
                className="btn btn-tmis-outline d-flex align-items-center gap-2"
                onClick={() => importFileRef.current.click()}
                disabled={importing}
              >
                {importing
                  ? <span className="spinner-border spinner-border-sm" />
                  : <i className="bi bi-upload" />}
                Import
              </button>
            </>
          )}
          {can('sessions', 'create') && (
            <button className="btn btn-tmis-primary d-flex align-items-center gap-2" onClick={() => openCreate()}>
              <i className="bi bi-plus-lg" />
              Create Attendance
            </button>
          )}
        </div>
      </div>

      {/* Date Picker / Upcoming toggle */}
      <div className="tmis-card attendance-filter-card mb-2 d-flex align-items-center gap-3 flex-wrap" style={{padding: '7px 24px'}}>
        <div className="filter-tab-group">
          {[
            { value: '', label: 'All' },
            { value: 'training', label: 'Training' },
            { value: 'assessment', label: 'Assessment' },
          ].map(opt => (
            <button
              key={opt.value || 'all'}
              type="button"
              className={`filter-tab ${sessionType === opt.value ? 'active' : ''}`}
              onClick={() => setSessionType(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
        {mode === 'date' ? (
          <>
            <div className="attendance-date-control d-flex align-items-center gap-2">
              <label className="fw-semibold mb-0" htmlFor="session-start-date"> <i className="bi bi-calendar3 text-muted" /> From:</label>
              <input
                id="session-start-date"
                type="date"
                className="form-control"
                style={{ width: 170 }}
                value={startDate}
                max={endDate}
                onChange={e => changeStartDate(e.target.value)}
              />
            </div>
            <div className="attendance-date-control d-flex align-items-center gap-2">
              <label className="fw-semibold mb-0" htmlFor="session-end-date"> <i className="bi bi-calendar-check text-muted" /> To:</label>
              <input
                id="session-end-date"
                type="date"
                className="form-control"
                style={{ width: 170 }}
                value={endDate}
                min={startDate}
                onChange={e => changeEndDate(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="btn btn-tmis-outline btn-sm attendance-upcoming-btn d-flex align-items-center gap-2 ms-auto"
              onClick={openUpcoming}
            >
              <i className="bi bi-calendar-event" />
              View Timetable
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="btn btn-tmis-outline btn-sm attendance-upcoming-btn d-flex align-items-center gap-2 ms-auto"
              onClick={() => setMode('date')}
            >
              <i className="bi bi-arrow-left" />
              Back to Calendar
            </button>
          </>
        )}
      </div>

      {/* Sessions List */}
      <div>
        {loading ? (
          <div className="text-center py-5"><div className="spinner-border" style={{ color: '#17a2b8' }} /></div>
        ) : mode === 'upcoming' ? (
          <div className="upcoming-timetable" aria-label="Timetable">
            <div className="upcoming-timetable-head">
              <div className="upcoming-timetable-head-title">
                <i className="bi bi-calendar-week" aria-hidden="true" />
                <div>
                  <span>Timetable</span>
                </div>
              </div>
              <div className="timetable-week-controls" aria-label="Timetable week navigation">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm timetable-week-arrow"
                  onClick={() => moveTimetableWeek(-1)}
                  disabled={!timetableCanGoPrevious}
                  aria-label="Previous week"
                >
                  <i className="bi bi-chevron-left" aria-hidden="true" />
                </button>
                <label className="timetable-week-picker" htmlFor="timetable-week-date">
                  <i className="bi bi-calendar3" aria-hidden="true" />
                  <span className="visually-hidden">Select a date in the week to display</span>
                  <input
                    id="timetable-week-date"
                    type="date"
                    value={timetableDatePickerValue}
                    min={timetableHistoryStart.format('YYYY-MM-DD')}
                    max={timetableFutureEnd.format('YYYY-MM-DD')}
                    onChange={e => selectTimetableDate(e.target.value)}
                  />
                </label>
                <span className="timetable-week-range">{timetableRangeLabel}</span>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm timetable-week-arrow"
                  onClick={() => moveTimetableWeek(1)}
                  disabled={!timetableCanGoNext}
                  aria-label="Next week"
                >
                  <i className="bi bi-chevron-right" aria-hidden="true" />
                </button>
              </div>
            </div>

            <div className="upcoming-timetable-meta">
              <span className="upcoming-timetable-legend"><i aria-hidden="true" />Scheduled</span>
              <span className="upcoming-timetable-legend is-ongoing"><i aria-hidden="true" />Ongoing</span>
              <span className="upcoming-timetable-legend is-ended"><i aria-hidden="true" />Ended</span>
              <span className="upcoming-timetable-legend is-past-date"><i aria-hidden="true" />Past dates shaded</span>
              <span className="upcoming-timetable-legend is-today-date"><i aria-hidden="true" />Today</span>
              <span className="upcoming-timetable-hint"><i className="bi bi-cursor" aria-hidden="true" /> Select a class to view details</span>
            </div>

            <div className="upcoming-timetable-body">
              <div
                className="timetable-grid"
                style={{
                  '--timetable-slot-count': timetableSlots.length,
                }}
              >
                <div className="timetable-grid-corner">Time</div>
                {timetableDays.map((day, dayIndex) => {
                  const dayState = timetableDayState(day);
                  const isToday = dayState === 'is-today';
                  return (
                    <div key={day.format('YYYY-MM-DD')} className={`timetable-grid-day-header ${dayState}`} style={{ gridColumn: dayIndex + 2 }}>
                      <span>{day.format('ddd')}</span>
                      <strong>{day.format('D')}</strong>
                      <small>{day.format('MMM')}</small>
                      {isToday && <em>Today</em>}
                    </div>
                  );
                })}

                <div className="timetable-time-column">
                  {timetableSlots.map(hour => (
                    <div className="timetable-time-slot" key={hour}>
                      {moment().hour(hour).minute(0).format('h:mm A')}
                    </div>
                  ))}
                </div>

                {timetableDays.map((day, dayIndex) => {
                  const dayKey = day.format('YYYY-MM-DD');
                  const daySessions = timetableSessionsByDate[dayKey] || [];
                  const eventLayouts = timetableEventLayoutsByDate[dayKey] || new Map();
                  const dayState = timetableDayState(day);
                  return (
                    <div key={dayKey} className={`timetable-day-column ${dayState}`} style={{ gridColumn: dayIndex + 2 }}>
                      {timetableSlots.map(hour => (
                        <button
                          key={hour}
                          type="button"
                          className={`timetable-grid-slot ${timetableSlotHasSession(daySessions, hour) ? 'has-session' : 'is-empty'}`}
                          style={{ gridRow: hour - timetableStartHour + 1 }}
                          onClick={() => {
                            if (!timetableSlotHasSession(daySessions, hour) && can('sessions', 'create')) {
                              openCreate(dayKey, `${String(hour).padStart(2, '0')}:00`);
                            }
                          }}
                          disabled={timetableSlotHasSession(daySessions, hour) || !can('sessions', 'create')}
                          aria-label={!timetableSlotHasSession(daySessions, hour) ? `Create session on ${day.format('MMMM D, YYYY')} at ${timetableTimeLabel(`${String(hour).padStart(2, '0')}:00`)}` : undefined}
                        >
                          {!timetableSlotHasSession(daySessions, hour) && <span>+</span>}
                        </button>
                      ))}
                      {daySessions.map((session, sessionIndex) => {
                        const selected = session.id === selectedTimetableSessionId;
                        const eventPosition = timetableEventPosition(session, eventLayouts.get(session.id));
                        const type = session.session_type === 'assessment' ? 'assessment' : 'training';
                        const status = timetableSessionStatus(session);
                        const statusLabel = timetableSessionStatusLabel(status);
                        const title = session.module_name || session.schedule_name || 'Untitled session';
                        const intakeLabel = formatTimetableIntakes(session.intakes);
                        return (
                          <button
                            key={session.id}
                            type="button"
                            className={`timetable-grid-event type-${type} status-${status} ${selected ? 'is-selected' : ''}`}
                            style={{ ...eventPosition, '--timetable-event-delay': `${Math.min((dayIndex * 45) + (sessionIndex * 65), 500)}ms` }}
                            onClick={() => setSelectedTimetableSessionId(selected ? null : session.id)}
                            aria-expanded={selected}
                            aria-controls={selected ? `timetable-details-${session.id}` : undefined}
                            aria-label={`${title}${intakeLabel ? `, intake ${intakeLabel}` : ''}, ${timetableTimeLabel(session.start_time)} to ${timetableTimeLabel(session.end_time)}`}
                            title="Select to view attendance and actions"
                          >
                            <span className="timetable-grid-event-topline">
                              <span>{statusLabel}</span>
                              <i className={`bi ${type === 'assessment' ? 'bi-clipboard2-check' : 'bi-mortarboard'}`} aria-hidden="true" />
                            </span>
                            <strong>{title}</strong>
                            {session.schedule_name && <small className="timetable-grid-event-code">Code: {session.schedule_name}</small>}
                            <span className="timetable-grid-event-type">{type === 'assessment' ? (assessmentBasisLabel(session.assessment_basis) || 'Assessment') : 'Training'}</span>
                            {session.trainer_name && <small className="timetable-grid-event-trainer"><i className="bi bi-person" aria-hidden="true" />{session.trainer_name}</small>}
                            {session.trainer_login_start && <small className="timetable-grid-event-login"><i className="bi bi-box-arrow-in-right" aria-hidden="true" />Login: {formatTimetableLoginTime(session.trainer_login_start)}</small>}
                            {session.venue_name && <small><i className="bi bi-geo-alt" aria-hidden="true" />{session.venue_name}</small>}
                            {intakeLabel && <small className="timetable-grid-event-intake"><i className="bi bi-people" aria-hidden="true" />Intake: {intakeLabel}</small>}
                            <small className="timetable-grid-event-time"><i className="bi bi-clock" aria-hidden="true" />{timetableTimeLabel(session.start_time)} - {timetableTimeLabel(session.end_time)}</small>
                          </button>
                        );
                      })}
                    </div>
                  );
                })}
              </div>

              <div className={`timetable-mobile-list ${timetableWeekSessions.length === 0 ? 'is-empty' : ''}`}>
                {timetableDays.map((day, dayIndex) => {
                  const dayKey = day.format('YYYY-MM-DD');
                  const daySessions = timetableSessionsByDate[dayKey] || [];
                  const dayState = timetableDayState(day);
                  const isToday = dayState === 'is-today';
                  return (
                    <section key={dayKey} className={`timetable-mobile-day ${dayState}`}>
                      <div className="timetable-mobile-day-heading">
                        <div className="timetable-mobile-date">
                          <strong className={`timetable-day-pill timetable-day-pill-${dayIndex}`}>{day.format('dddd')}</strong>
                          <span>{day.format('D MMM')}</span>
                        </div>
                        {isToday && <span className="timetable-today-badge">Today</span>}
                        <span className="timetable-mobile-count">{daySessions.length} {daySessions.length === 1 ? 'session' : 'sessions'}</span>
                      </div>
                      {daySessions.length > 0 ? daySessions.map((session, sessionIndex) => {
                        const selected = session.id === selectedTimetableSessionId;
                        const type = session.session_type === 'assessment' ? 'assessment' : 'training';
                        const status = timetableSessionStatus(session);
                        const title = session.module_name || session.schedule_name || 'Untitled session';
                        const intakeLabel = formatTimetableIntakes(session.intakes);
                        return (
                          <button
                            key={session.id}
                            type="button"
                            className={`timetable-mobile-event type-${type} status-${status} ${selected ? 'is-selected' : ''}`}
                            style={{ '--timetable-event-delay': `${Math.min((dayIndex * 45) + (sessionIndex * 65), 500)}ms` }}
                            onClick={() => setSelectedTimetableSessionId(selected ? null : session.id)}
                            aria-expanded={selected}
                            aria-controls={selected ? `timetable-details-${session.id}` : undefined}
                            aria-label={`${title}${intakeLabel ? `, intake ${intakeLabel}` : ''}, ${timetableTimeLabel(session.start_time)} to ${timetableTimeLabel(session.end_time)}`}
                          >
                            <span className="timetable-mobile-event-time-group">
                              {intakeLabel && <small className="timetable-mobile-event-intake"><i className="bi bi-people" aria-hidden="true" />Intake: {intakeLabel}</small>}
                              <span className="timetable-mobile-event-time">
                                <i className="bi bi-clock" aria-hidden="true" />
                                {timetableTimeLabel(session.start_time)} - {timetableTimeLabel(session.end_time)}
                              </span>
                            </span>
                            <span className="timetable-mobile-event-main">
                              <strong>{title}</strong>
                              <small>{type === 'assessment' ? (assessmentBasisLabel(session.assessment_basis) || 'Assessment') : 'Training'}{session.trainer_name ? ` · ${session.trainer_name}` : ''}{session.venue_name ? ` · ${session.venue_name}` : ''}</small>
                              {session.trainer_login_start && <small className="timetable-mobile-event-login"><i className="bi bi-box-arrow-in-right" aria-hidden="true" />Login: {formatTimetableLoginTime(session.trainer_login_start)}</small>}
                            </span>
                            <i className={`bi bi-chevron-right ${selected ? 'is-expanded' : ''}`} aria-hidden="true" />
                          </button>
                        );
                      }) : (
                        <div className="timetable-mobile-day-empty">No sessions scheduled</div>
                      )}
                      {timetableSelectedSession?.session_date === dayKey && renderTimetableDetails('timetable-details-mobile')}
                    </section>
                  );
                })}
              </div>

              {timetableWeekSessions.length === 0 && (
                <div className="timetable-week-empty">
                  <i className="bi bi-calendar2-week" aria-hidden="true" />
                  <div>
                    <strong>No classes this week</strong>
                    <span>Use the arrows above to view another week.</span>
                  </div>
                </div>
              )}

              {renderTimetableDetails('timetable-details-desktop')}
            </div>
          </div>
        ) : sessions.length === 0 ? (
          <div className="empty-state">
            <i className="bi bi-calendar-x" />
            <h5>
              {startDate === endDate
                ? `No sessions on ${moment(startDate).format('DD/MM/YYYY')}`
                : `No sessions from ${moment(startDate).format('DD/MM/YYYY')} to ${moment(endDate).format('DD/MM/YYYY')}`}
            </h5>
            <p style={{ fontSize: '0.875rem' }}>
              Click <strong>Create Attendance</strong> to schedule a class{startDate === endDate ? ' for this date' : ''}.
            </p>
          </div>
        ) : (
          <div className="d-flex flex-column" style={{ gap: 0 }}>
            {sessions.map((s, idx) => (
              <React.Fragment key={s.id}>
                {startDate !== endDate && (idx === 0 || sessions[idx - 1].session_date !== s.session_date) && (
                  <div className="d-flex align-items-baseline gap-2" style={{ padding: idx === 0 ? '4px 0' : '14px 0 4px' }}>
                    <span className="fw-semibold">{moment(s.session_date).format('DD MMM YYYY')}</span>
                    <span className="text-muted" style={{ fontSize: '0.8rem' }}>· {moment(s.session_date).format('dddd')}</span>
                  </div>
                )}
                {idx > 0 && sessions[idx - 1].session_date === s.session_date && <div className="session-list-divider" />}
                <div style={{ padding: '8px 0' }}>
                  <SessionCard
                    session={s}
                    onEdit={() => openEdit(s)}
                    onDelete={() => handleDelete(s.id)}
                    onDuplicate={() => handleDuplicate(s.id)}
                    onEnd={() => handleEndClass(s.id)}
                    canEdit={can('sessions', 'update')}
                    canDelete={can('sessions', 'delete')}
                    canCreate={can('sessions', 'create')}
                    user={user}
                  />
                </div>
              </React.Fragment>
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <SessionModal
          user={user}
          session={editSession}
          initialDate={createDefaults?.date}
          initialStartTime={createDefaults?.startTime}
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); fetchSessions(); }}
        />
      )}

      {importPreview && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-file-earmark-spreadsheet me-2" />
                  Review Import
                </h5>
                <button className="btn-close btn-close-white" onClick={() => setImportPreview(null)} disabled={importChecking || importSaving} />
              </div>
              <div className="modal-body">
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
                  <div>
                    <div className="fw-semibold">{importPreview.filename || 'Uploaded file'}</div>
                    <div className="text-muted small">
                      {importRows.length} row{importRows.length === 1 ? '' : 's'} found
                      {importAttentionCount > 0 ? ` · ${importAttentionCount} need attention` : ''}
                    </div>
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    <span className="import-summary-pill" style={{ background: 'var(--tmis-pill-present-bg)', color: 'var(--tmis-pill-present-text)' }}>
                      <i className="bi bi-check-circle-fill me-1" />Ready: {importRows.filter(r => r.status === 'ready').length}
                    </span>
                    <span className="import-summary-pill" style={{ background: 'var(--tmis-pill-absent-bg)', color: 'var(--tmis-pill-absent-text)' }}>
                      <i className="bi bi-x-circle-fill me-1" />Needs Fix: {importRows.filter(r => ['missing', 'error'].includes(r.status)).length}
                    </span>
                    <span className="import-summary-pill" style={{ background: 'var(--tmis-pill-late-bg)', color: 'var(--tmis-pill-late-text)' }}>
                      <i className="bi bi-exclamation-triangle-fill me-1" />Conflicts: {importPreview.conflicts?.length || 0}
                    </span>
                  </div>
                </div>

                {importHasLocalMissing && (
                  <div className="alert alert-danger py-2 px-3">
                    <i className="bi bi-exclamation-circle me-1" />
                    Complete the required fields and make sure end time is later than start time before proceeding.
                  </div>
                )}

                {importPreview.checked && !importPreview.canImport && (
                  <div className="alert alert-warning py-2 px-3">
                    <i className="bi bi-exclamation-triangle me-1" />
                    Some rows still have errors or calendar conflicts. Please update them, then check availability again.
                  </div>
                )}

                <div className="import-review-cards">
                  {importRows.map(row => {
                    const isExpanded = expandedImportRows.has(row.row);
                    return (
                    <div
                      key={row.row}
                      className={`import-row-card ${isExpanded ? 'is-expanded' : ''}`}
                    >
                      <button
                        type="button"
                        className="import-row-card-heading"
                        onClick={() => toggleImportRow(row.row)}
                        aria-expanded={isExpanded}
                      >
                        <span className="import-row-card-heading-main">
                          <i className={`bi ${isExpanded ? 'bi-chevron-down' : 'bi-chevron-right'} import-row-chevron`} aria-hidden="true" />
                          <span className="fw-bold">Row {row.row}</span>
                          {!isExpanded && (
                            <span className="import-row-summary">
                              {[row.intake, row.module, row.venue].filter(Boolean).join(' · ') || 'No details yet'}
                              {row.session_date && ` · ${moment(row.session_date).format('DD MMM')}`}
                              {row.start_time && ` ${row.start_time.slice(0, 5)}`}
                            </span>
                          )}
                        </span>
                        <ImportStatusPill row={row} checked={importPreview.checked} />
                      </button>

                      {isExpanded && (
                      <div className="import-row-card-body">
                      <div className="import-row-card-grid">
                        <label>
                          <span className="import-field-label"><i className="bi bi-people-fill" aria-hidden="true" />Intake {!row.intake && <b aria-label="required">*</b>}</span>
                          <Select
                            options={row.intake && !importIntakeOptions.some(option => option.value === row.intake)
                              ? [{ value: row.intake, label: `${row.intake} (not available)` }, ...importIntakeOptions]
                              : importIntakeOptions}
                            value={row.intake ? { value: row.intake, label: row.intake } : null}
                            onChange={option => updateImportRow(row.row, 'intake', option?.value || '')}
                            isDisabled={!!row.intake_provided}
                            isSearchable
                            placeholder="Search intake..."
                            classNamePrefix="react-select"
                          />
                        </label>
                        <label>
                          <span className="import-field-label"><i className="bi bi-book-fill" aria-hidden="true" />Module {!row.module && <b aria-label="required">*</b>}</span>
                          <Select
                            options={importModulesFor(row.intake)}
                            value={row.module ? { value: row.module, label: importModuleLabelFor(row.intake, row.module) } : null}
                            onChange={option => updateImportRow(row.row, 'module', option?.value || '')}
                            isDisabled={!!row.module_provided}
                            isSearchable
                            placeholder={row.intake ? 'Search module...' : 'Select intake first...'}
                            classNamePrefix="react-select"
                          />
                        </label>
                        <label>
                          <span className="import-field-label"><i className="bi bi-geo-alt-fill" aria-hidden="true" />Venue {!row.venue && <b aria-label="required">*</b>}</span>
                          <Select
                            options={row.venue && !importVenueOptions.some(option => option.value === row.venue)
                              ? [{ value: row.venue, label: `${row.venue} (not available)` }, ...importVenueOptions]
                              : importVenueOptions}
                            value={row.venue ? { value: row.venue, label: row.venue } : null}
                            onChange={option => updateImportRow(row.row, 'venue', option?.value || '')}
                            isDisabled={!!row.venue_provided}
                            isSearchable
                            placeholder="Search venue..."
                            classNamePrefix="react-select"
                          />
                        </label>
                        <label>
                          <span className="import-field-label"><i className="bi bi-person-badge-fill" aria-hidden="true" />Trainer {!row.trainer && <b aria-label="required">*</b>}</span>
                          <Select
                            options={row.trainer && !importTrainerOptions.some(option => option.value === row.trainer)
                              ? [{ value: row.trainer, label: `${row.trainer} (not available)` }, ...importTrainerOptions]
                              : importTrainerOptions}
                            value={row.trainer ? { value: row.trainer, label: row.trainer } : null}
                            onChange={option => updateImportRow(row.row, 'trainer', option?.value || '')}
                            isDisabled={!!row.trainer_provided}
                            isSearchable
                            isClearable
                            placeholder="Search trainer..."
                            classNamePrefix="react-select"
                          />
                        </label>
                      </div>

                      {/* Own flex row so Date/Start/End always fill their own
                          line's width evenly, instead of sharing track sizing
                          with the 4-field group above and leaving a gap. */}
                      <div className="import-row-card-grid import-row-card-grid-dates">
                        <label>
                          <span className="import-field-label"><i className="bi bi-calendar3" aria-hidden="true" />Date {!row.session_date && <b aria-label="required">*</b>}</span>
                          <input type="date" className="form-control" value={(row.session_date || '').slice(0, 10)} onChange={e => updateImportRow(row.row, 'session_date', e.target.value)} disabled={!!row.session_date_provided} />
                        </label>
                        <label>
                          <span className="import-field-label"><i className="bi bi-clock-fill" aria-hidden="true" />Start {!row.start_time && <b aria-label="required">*</b>}</span>
                          <input type="time" className="form-control" value={(row.start_time || '').slice(0, 5)} onChange={e => updateImportRow(row.row, 'start_time', e.target.value)} disabled={!!row.start_time_provided} />
                        </label>
                        <label>
                          <span className="import-field-label"><i className="bi bi-clock-history" aria-hidden="true" />End {!row.end_time && <b aria-label="required">*</b>}</span>
                          <input type="time" className="form-control" value={(row.end_time || '').slice(0, 5)} onChange={e => updateImportRow(row.row, 'end_time', e.target.value)} disabled={!!row.end_time_provided} />
                        </label>
                      </div>

                      <div className="import-row-settings">
                        <div className="import-setting-block">
                          <span className="import-field-label"><i className="bi bi-ui-radios" aria-hidden="true" />Session Type {!row.session_type && <b aria-label="required">*</b>}</span>
                          <div className="session-type-choices import-type-choices" role="radiogroup" aria-label="Session type">
                            {[
                              { value: 'training', label: 'Training', icon: 'bi-mortarboard-fill' },
                              { value: 'assessment', label: 'Assessment', icon: 'bi-clipboard2-check-fill' },
                            ].map(opt => (
                              <button
                                key={opt.value}
                                type="button"
                                className={`session-type-choice ${row.session_type === opt.value ? 'is-selected' : ''}`}
                                onClick={() => updateImportRow(row.row, 'session_type', opt.value)}
                              >
                                <i className={`bi ${opt.icon}`} aria-hidden="true" />
                                <span className="fw-semibold">{opt.label}</span>
                                {row.session_type === opt.value && <i className="bi bi-check-circle-fill ms-auto" aria-hidden="true" />}
                              </button>
                            ))}
                          </div>
                        </div>
                        {row.session_type === 'assessment' && (
                          <div className="session-auto-present-panel import-auto-present-panel">
                            <label className="session-toggle" htmlFor={`force_present_${row.row}`}>
                              <input
                                className="session-toggle-input"
                                type="checkbox"
                                role="switch"
                                id={`force_present_${row.row}`}
                                checked={!!row.force_present}
                                disabled={row.force_present_provided}
                                onChange={e => updateImportRow(row.row, 'force_present', e.target.checked)}
                              />
                              <span className="session-toggle-track" aria-hidden="true"><span /></span>
                            </label>
                            <label htmlFor={`force_present_${row.row}`} className="session-auto-present-copy">
                              <span className="fw-semibold">Mark all as Present on scan</span>
                              <br />
                              Skip the late/absent time-window rules for this session — any student who scans the QR is marked Present, no matter when. Students who never scan are still marked Absent.
                            </label>
                          </div>
                        )}
                        {row.session_type === 'assessment' && (
                          <div className="import-setting-block import-basis-block">
                            <span className="import-field-label"><i className="bi bi-ui-radios" aria-hidden="true" />Assessment Basis</span>
                            <AssessmentBasisPicker
                              name={`assessment_basis_${row.row}`}
                              value={row.assessment_basis}
                              onChange={v => updateImportRow(row.row, 'assessment_basis', v)}
                            />
                          </div>
                        )}
                      </div>

                      {(row.missing?.length > 0 || row.errors?.length > 0 || row.conflicts?.length > 0) && (
                        <div className="import-row-message">
                          {row.missing?.length > 0 && <div>Missing: {row.missing.join(', ')}</div>}
                          {row.errors?.length > 0 && <div>{row.errors.join(' ')}</div>}
                          {row.conflicts?.length > 0 && <div>Venue is booked for this date and time.</div>}
                        </div>
                      )}
                      </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              </div>
              <div className="modal-footer import-review-footer d-flex justify-content-end flex-wrap gap-2">
                <div className="import-review-footer-actions d-flex gap-2 flex-wrap">
                  <span className={`import-review-footer-hint ${importPreview.checked && importPreview.canImport ? 'is-ready' : ''}`} role="status">
                    <i className={`bi ${importPreview.checked && importPreview.canImport ? 'bi-check-circle-fill' : 'bi-info-circle-fill'}`} aria-hidden="true" />
                    {importPreview.checked && importPreview.canImport
                      ? 'Availability checked. You can proceed with the import.'
                      : 'Check availability before proceeding with the import.'}
                  </span>
                  <button
                    className="btn btn-tmis-outline d-flex align-items-center justify-content-center gap-2"
                    onClick={handleCheckImportAvailability}
                    disabled={!importCanProceed}
                  >
                    {importChecking ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-calendar-check" />}
                    <span>Check Availability</span>
                  </button>
                  <button
                    className="btn btn-tmis-primary d-flex align-items-center justify-content-center gap-2"
                    onClick={handleConfirmImport}
                    disabled={!importCanSave}
                  >
                    {importSaving ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-upload" />}
                    <span>Import Sessions</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {importResult && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-check-circle-fill me-2" style={{ color: 'var(--tmis-pill-present-text)' }} />
                  Import Complete
                </h5>
                <button className="btn-close btn-close-white" onClick={() => setImportResult(null)} />
              </div>
              <div className="modal-body text-center py-4">
                <i className="bi bi-clipboard2-check-fill" style={{ fontSize: '2.5rem', color: 'var(--tmis-pill-present-text)' }} />
                <h6 className="mt-3 mb-1">
                  {importResult.inserted} session{importResult.inserted === 1 ? '' : 's'} created
                </h6>
                <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                  {importResult.message || 'Your sessions have been added to the schedule.'}
                </p>
              </div>
              <div className="modal-footer justify-content-center">
                <button className="btn btn-tmis-primary" onClick={() => setImportResult(null)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {importEmpty && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-file-earmark-x me-2" />
                  Empty File
                </h5>
                <button className="btn-close btn-close-white" onClick={() => setImportEmpty(null)} />
              </div>
              <div className="modal-body text-center py-4">
                <i className="bi bi-file-earmark-excel" style={{ fontSize: '2.5rem', color: 'var(--tmis-muted)' }} />
                <h6 className="mt-3 mb-1">{importEmpty.filename || 'This file'} has no data rows</h6>
                <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                  Download the template, fill in at least one session, and try importing again.
                </p>
              </div>
              <div className="modal-footer justify-content-center">
                <button className="btn btn-tmis-primary" onClick={() => setImportEmpty(null)}>Got it</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
